/**
 * Teams Constants
 * 
 * This file contains all constants used in team-related components
 * including UI configurations, table columns, and helper functions.
 */

import { ICONS } from './icons'

// ============================================================================
// UI CONFIGURATION
// ============================================================================

/**
 * Teams UI configuration
 */
export const TEAMS_UI_CONFIG = {
  MAX_VISIBLE_PROJECTS: 2,
  MAX_VISIBLE_MEMBERS: 3,
  MODAL_SIZE: 'lg',
  MODAL_CUSTOM_WIDTH: '800px',
  AVATAR_SIZE: 10,
  AVATAR_DEFAULT_LETTER: 'T',
  TABLE_PAGE_SIZE: 10
}

/**
 * Team Details UI configuration
 */
export const TEAM_DETAILS_UI_CONFIG = {
  MAX_VISIBLE_PROJECTS: 5,
  AVATAR_SIZE: 10,
  TABLE_PAGE_SIZE: 10
}

// ============================================================================
// TABLE COLUMNS
// ============================================================================

/**
 * Get teams table columns configuration
 * @param {Function} t - Translation function
 * @returns {Array} Column configuration array
 */
export function getTeamsTableColumns(t) {
  return [
    { key: 'team', label: t('teams.columns.team'), sortable: true },
    { key: 'description', label: t('teams.columns.description'), sortable: false },
    { key: 'projects', label: t('teams.columns.projects'), sortable: true },
    { key: 'created', label: t('teams.columns.created'), sortable: true },
    { key: 'actions', label: t('teams.columns.actions'), align: 'center', sortable: false }
  ]
}

/**
 * Get team projects table columns configuration  
 * @param {Function} t - Translation function
 * @returns {Array} Column configuration array
 */
export function getTeamProjectsTableColumns(t) {
  return [
    { key: 'project', label: t('teams.projectColumns.project'), sortable: true },
    { key: 'description', label: t('teams.projectColumns.description'), sortable: false },
    { key: 'status', label: t('teams.projectColumns.status'), sortable: true },
    { key: 'created', label: t('teams.projectColumns.created'), sortable: true },
    { key: 'actions', label: t('teams.projectColumns.actions'), align: 'center', sortable: false }
  ]
}

// ============================================================================
// STATUS AND BADGE HELPERS
// ============================================================================

/**
 * Get badge variant for project status
 * @param {string} status - Project status
 * @returns {string} Badge variant name
 */
export function getProjectStatusBadgeVariant(status) {
  const variants = {
    active: 'success',
    paused: 'warning',
    completed: 'info',
    archived: 'gray'
  }
  return variants[status?.toLowerCase()] || 'gray'
}

/**
 * Format project status for display
 * @param {string} status - Project status
 * @param {Function} t - Translation function
 * @returns {string} Formatted status
 */
export function formatProjectStatus(status, t) {
  if (!status) return t('common.unknown')
  return t(`project.status.${status.toLowerCase()}`)
}

/**
 * Format project type for display
 * @param {string} type - Project type
 * @param {Function} t - Translation function
 * @returns {string} Formatted type
 */
export function formatProjectType(type, t) {
  if (!type) return t('common.unknown')
  return t(`project.type.${type.toLowerCase()}`)
}

/**
 * Format visibility for display
 * @param {string} visibility - Visibility setting
 * @param {Function} t - Translation function
 * @returns {string} Formatted visibility
 */
export function formatVisibility(visibility, t) {
  if (!visibility) return t('common.visibility.public')
  return t(`common.visibility.${visibility.toLowerCase()}`)
}

// ============================================================================
// DELETION OPTIONS
// ============================================================================

/**
 * Team deletion migration options
 */
export const TEAM_DELETION_OPTIONS = {
  MIGRATE: 'migrate',
  DELETE: 'delete'
}

/**
 * Get team deletion option configurations
 * @param {Function} t - Translation function
 * @returns {Array} Deletion option configurations
 */
export function getTeamDeletionOptions(t) {
  return [
    {
      value: TEAM_DELETION_OPTIONS.MIGRATE,
      label: t('teams.deletion.migrateLabel'),
      description: t('teams.deletion.migrateDescription'),
      variant: 'primary'
    },
    {
      value: TEAM_DELETION_OPTIONS.DELETE,
      label: t('teams.deletion.deleteLabel'),
      description: t('teams.deletion.deleteDescription'),
      variant: 'danger'
    }
  ]
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get active projects count for a team
 * @param {Object} team - Team object
 * @param {Array} projects - Array of all projects
 * @returns {number} Count of active projects
 */
export function getActiveProjectsCount(team, projects) {
  if (team.activeProjectsCount !== undefined) {
    return team.activeProjectsCount
  }
  
  // If we don't have the projects dataset, fall back to the team's own list
  if (!projects) {
    return team.projects?.length || 0
  }
  
  // Prefer authoritative linkage via project.teamId; fall back to team.projects (ids) if present
  return projects.filter(p => {
    const linkedByTeamId = p.teamId === team.id
    const linkedByIds = Array.isArray(team.projects) && team.projects.includes(p.id)
    const notDeleted = !p.is_deleted
    return (linkedByTeamId || linkedByIds) && notDeleted
  }).length
}

/**
 * Get team member names
 * @param {Array} memberIds - Array of member IDs
 * @param {Array} users - Array of all users
 * @returns {Array} Array of member names
 */
export function getTeamMemberNames(memberIds, users) {
  if (!memberIds || !users) return []
  
  return memberIds
    .map(memberId => {
      const user = users.find(u => u.id === memberId)
      return user?.name
    })
    .filter(Boolean)
}

/**
 * Format date for display
 * @param {Date|string} date - Date to format
 * @param {Function} t - Translation function
 * @returns {string} Formatted date
 */
export function formatDate(date, t) {
  if (!date) return t('common.notAvailable')
  return new Date(date).toLocaleDateString()
}

/**
 * Check if user can access team (based on visibility and membership)
 * @param {Object} team - Team object
 * @param {Object} userProfile - Current user profile
 * @returns {boolean} True if user has access
 */
export function canAccessTeam(team, userProfile) {
  if (!team) return false
  
  const isSuperAdmin = userProfile?.role?.toLowerCase() === 'super_admin'
  
  // Super Admin can access all teams
  if (isSuperAdmin) return true
  
  // Public teams are accessible to everyone
  if (team.visibility !== 'private') return true
  
  // Private teams are only accessible to members
  const currentUserId = userProfile?.id
  return team.members && team.members.includes(currentUserId)
}

/**
 * Check if user can access project (based on visibility and membership)
 * @param {Object} project - Project object
 * @param {Object} userProfile - Current user profile
 * @returns {boolean} True if user has access
 */
export function canAccessProject(project, userProfile) {
  if (!project) return false

  const isSuperAdmin = userProfile?.role?.toLowerCase() === 'super_admin'

  // Super Admin can access all projects
  if (isSuperAdmin) return true

  // Public projects are accessible to everyone
  if (project.visibility !== 'private') return true

  // Private projects are only accessible to members.
  // The /projects list endpoint hydrates members as { id, name } objects, while
  // other callers (e.g. detail fetches) leave members as raw id strings — handle both.
  const currentUserId = userProfile?.id
  if (!currentUserId || !Array.isArray(project.members)) return false
  return project.members.some(
    (m) => m === currentUserId || (m && typeof m === 'object' && m.id === currentUserId),
  )
}

/**
 * Filter visible teams based on user permissions
 * @param {Array} teams - Array of all teams
 * @param {Object} userProfile - Current user profile
 * @returns {Array} Filtered teams array
 */
export function getVisibleTeams(teams, userProfile) {
  if (!teams) return []
  
  return teams.filter(team => canAccessTeam(team, userProfile))
}

/**
 * Filter visible projects based on user permissions
 * @param {Array} projects - Array of all projects
 * @param {Object} userProfile - Current user profile
 * @returns {Array} Filtered projects array
 */
export function getVisibleProjects(projects, userProfile) {
  if (!projects) return []
  
  return projects.filter(project => canAccessProject(project, userProfile))
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  TEAMS_UI_CONFIG,
  TEAM_DETAILS_UI_CONFIG,
  TEAM_DELETION_OPTIONS,
  getTeamsTableColumns,
  getTeamProjectsTableColumns,
  getProjectStatusBadgeVariant,
  formatProjectStatus,
  formatProjectType,
  formatVisibility,
  getTeamDeletionOptions,
  getActiveProjectsCount,
  getTeamMemberNames,
  formatDate,
  canAccessTeam,
  canAccessProject,
  getVisibleTeams,
  getVisibleProjects
}

